import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router';
import { useAuthentication, useChannels } from '@connect-x-app/hybrid-state';
import { Dot } from '@connect-x-app/hybrid-ui';
import { hasMemberChannelUnread } from '@connect-x-app/shared-utils';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import classNames from 'classnames';

import './ChannelTabs.scss';

export const ChannelTabs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { roomId } = useParams<{ roomId: string }>();

  const { roomChannels } = useChannels();
  const { isUserPartOfChannel } = useAuthentication();

  const isChannelsPath = useMemo(() => !!matchPath({ path: '/dashboard/flightrooms/:roomId/channel/*' }, pathname), [pathname]);

  if (!roomChannels?.customChannels?.length) {
    return null;
  }

  const customChannelsCount = roomChannels.customChannels.length;
  const mainChannelUnreadCount = roomChannels.mainChannel?.unreadMessagesCount ?? 0;
  const customChannelsHaveUnread = hasMemberChannelUnread(roomChannels.customChannels, isUserPartOfChannel);

  const handleTabClick = (isCustomChannels?: boolean) => {
    const path = isCustomChannels
      ? `/dashboard/flightrooms/${roomId}/channel`
      : `/dashboard/flightrooms/${roomId}/${roomChannels.mainChannel.id}`;
    navigate(path);
  };

  return (
    <div className="channel-tabs">
      <button
        className={classNames('channel-tabs__tab', { 'channel-tabs__tab--active': !isChannelsPath })}
        data-test={CHANNEL_TEST_IDS.channels.mainTab}
        onClick={() => handleTabClick()}
        type="button"
      >
        {t('CHAT.CHANNELS.TABS.MAIN_ROOM')}
        {mainChannelUnreadCount > 0 ? (
          <Dot className="unread-count" size="xs" testId={CHANNEL_TEST_IDS.channels.mainTabUnreadDot} type="light-border" />
        ) : null}
      </button>
      <button
        className={classNames('channel-tabs__tab', { 'channel-tabs__tab--active': isChannelsPath })}
        data-test={CHANNEL_TEST_IDS.channels.customTab}
        onClick={() => handleTabClick(true)}
        type="button"
      >
        {t('CHAT.CHANNELS.TABS.CHANNELS_COUNT', { count: customChannelsCount })}
        {customChannelsHaveUnread ? (
          <Dot className="unread-count" size="xs" testId={CHANNEL_TEST_IDS.channels.customTabUnreadDot} type="light-border" />
        ) : null}
      </button>
    </div>
  );
};
